import { secureStorage } from './secureStorage';
import { env } from '../config/env';

/**
 * Download an analysis artifact from the backend.
 *
 * The artifact endpoint (`/v1/execute-ai/analysis-artifacts/{id}/download`)
 * is protected by Bearer auth, so a plain `<a download>` tag does not work
 * — the browser would not attach the token. We fetch the file with the
 * stored access token, wrap the response into a Blob and trigger a
 * synthetic anchor click so the browser shows the native "Save as" flow.
 *
 * Throws when the request fails so the caller can surface a toast/error.
 */
export const downloadAnalysisArtifact = async (
  downloadUrl: string,
  filename: string
): Promise<void> => {
  const baseUrl = env.REACT_APP_API_URL;
  const resolvedUrl = /^https?:\/\//i.test(downloadUrl)
    ? downloadUrl
    : `${baseUrl}${downloadUrl.startsWith('/') ? '' : '/'}${downloadUrl}`;
  const token = secureStorage.getItem('access_token');

  const response = await fetch(resolvedUrl, {
    method: 'GET',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    throw new Error(`download failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1500);
};
